/**
 * useOrderStore – Order state machine for TableOps
 *
 * Pure React context + useReducer.  No external deps.
 *
 * State:
 *   tables: Map<scopeId, TableOrder[]>   (history of orders per table)
 *   activeScope: string                  (currently selected table e.g. "T1")
 *
 * Actions mirror the KDS workflow:
 *   DISPATCH   → create order → split into station tickets → set in_progress
 *   BUMP       → advance a single ticket to the next status
 *   BUMP_LINE  → (optional) mark a single line done within a ticket
 *   CLEAR      → remove closed orders from view
 */

import React, {
  createContext,
  type ReactNode,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react'

import {
  type TableOrder,
  type Ticket,
  type StationId,
  type TicketStatus,
  type FulfillmentInfo,
  type FulfillmentStage,
  dispatchOrder,
  deriveFulfillmentStage,
  deriveOrderStatus,
  nextTicketStatus,
  stampFulfillmentStage,
} from './orderTypes'
import type { MergeRecord, RegisterShift, RegisterConfig, PaymentMethod, CheckPayment, TableCloseRecord, ItemSummary } from './types'
import { DEFAULT_PAYMENT_METHODS } from './types'
import {
  bumpTicketApi,
  fetchRegisterCatalog,
  fetchPaymentMethodCatalog,
  registerFulfillmentOrder,
  registerFulfillmentStage,
  registerPaymentOutcome,
  voidOrderLineApi,
  type OperationsSnapshot,
} from './api/tableOpsApi'
import { useOperationalStream } from './hooks/useOperationalStream'

/* ── State shape ────────────────────────────────────────────────────── */

/** Active session for an open table */
export interface TableSession {
  session_key: string
  table_key: string
  opened_at: string
  checks: number
  covers: number
}

interface OrderState {
  /** All orders keyed by scope (table) id, newest first */
  tables: Record<string, TableOrder[]>
  /** Currently focused table */
  activeScope: string
  /** Number of checks (split bills) for each open scope */
  checksPerScope: Record<string, number>
  /** Which check tab is currently selected per scope (0-based) */
  activeCheck: Record<string, number>
  /** Active hot table sessions keyed by scopeId (table label) */
  sessions: Record<string, TableSession>
  /** Persisted table merges (survive navigation) */
  merges: MergeRecord[]
  /** Active register (cashier shift) — null means no register open */
  register: RegisterShift | null
  /** Register scope family configurations */
  registerConfigs: RegisterConfig[]
  /** Currently active register number (default 1) */
  activeRegisterNum: number
  /** Closed shifts with analytics snapshot */
  closedShifts: RegisterShift[]
  /** Configured payment methods (global catalog) */
  paymentMethods: PaymentMethod[]
  /** Payments recorded per scope → check */
  payments: Record<string, CheckPayment[]>
  /** Closed table records (current shift revenue history) */
  closedTables: TableCloseRecord[]
}

const INITIAL_STATE: OrderState = {
  tables: {},
  activeScope: 'T1',
  checksPerScope: {},
  activeCheck: {},
  sessions: {},
  merges: [],
  register: null,
  registerConfigs: [],        // hydrated from entity catalog (tableops.register)
  activeRegisterNum: 1,
  closedShifts: [],
  paymentMethods: [],         // populated from DEFAULT_PAYMENT_METHODS on first load
  payments: {},
  closedTables: [],
}

/**
 * Detect synthetic fulfillment scopes (takeaway/delivery). They still need
 * merge-safe handling because the local optimistic row can appear before the
 * durable server snapshot catches up.
 */
function isFulfillmentScope(scopeId: string): boolean {
  return /^(TA|DL)-/.test(scopeId)
}

/** Ranking used to keep the most-advanced ticket status when merging. */
const TICKET_STATUS_RANK: Record<TicketStatus, number> = {
  open: 0,
  in_progress: 1,
  ready: 2,
  delivered: 3,
  closed: 4,
  discarded: 4,
}

const FULFILLMENT_STAGE_RANK: Record<FulfillmentStage, number> = {
  placed: 0,
  accepted: 1,
  ready: 2,
  handed_off: 3,
  completed: 4,
}

/**
 * Content signature for an order — id-independent so an optimistic local order
 * (random id) dedupes against its server-reconstructed twin (deterministic id)
 * by composition rather than identity.
 */
function orderSignature(order: TableOrder): string {
  const lines = order.tickets
    .flatMap(t => t.lines)
    .map((l) => {
      // Use display name as the canonical identity for dedupe so optimistic
      // local rows (name-only) match durable server rows (item_key present).
      const stableItemId = String(l.name || l.item_key || '').trim().toLowerCase()
      const stableNote = (l.note ?? '').trim().toLowerCase()
      return `${stableItemId}|${l.price}|${stableNote}x${l.quantity}`
    })
    .sort()
    .join(',')
  return `${order.checkIndex}|${lines}`
}

function parseIsoMs(value?: string | null): number {
  if (!value) return NaN
  return Date.parse(value)
}

function maxFinite(values: number[]): number {
  let best = NaN
  for (const v of values) {
    if (!Number.isFinite(v)) continue
    if (!Number.isFinite(best) || v > best) best = v
  }
  return best
}

function orderFreshnessMs(order: TableOrder): number {
  const ticketTimes = order.tickets.flatMap(t => [
    parseIsoMs(t.created_at),
    parseIsoMs(t.updated_at),
    parseIsoMs(t.bumped_at),
  ])
  return maxFinite([
    parseIsoMs(order.created_at),
    parseIsoMs(order.updated_at),
    ...ticketTimes,
  ])
}

function scopeFreshnessMs(current: OrderState, scopeId: string): number {
  const orderTimes = (current.tables[scopeId] ?? []).map(orderFreshnessMs)
  const paymentTimes = (current.payments[scopeId] ?? []).map(p => parseIsoMs(p.paid_at))
  return maxFinite([
    parseIsoMs(current.sessions[scopeId]?.opened_at),
    ...orderTimes,
    ...paymentTimes,
  ])
}

function orderSessionKey(orderId: string): string {
  const scopedOrderId = String(orderId || '').split('::')[0]
  const marker = '--ord-'
  const idx = scopedOrderId.indexOf(marker)
  if (idx <= 0) return ''
  return scopedOrderId.slice(0, idx)
}

/**
 * Merge two ticket lists for the same order, keeping the most-advanced status
 * per station and preserving any local bump timestamp (optimistic bump).
 */
function mergeOrderTickets(serverTickets: Ticket[], localTickets: Ticket[]): { tickets: Ticket[]; changed: boolean } {
  const localByStation = new Map<StationId, Ticket>()
  for (const t of localTickets) localByStation.set(t.station, t)
  let changed = false
  const tickets = serverTickets.map(st => {
    const lt = localByStation.get(st.station)
    if (!lt) return st
    const serverRank = TICKET_STATUS_RANK[st.status] ?? 0
    const localRank = TICKET_STATUS_RANK[lt.status] ?? 0
    if (localRank > serverRank) {
      changed = true
      return { ...st, status: lt.status, bumped_at: lt.bumped_at ?? st.bumped_at, updated_at: lt.updated_at }
    }
    return st
  })
  return { tickets, changed }
}

/**
 * Keep optimistic fulfillment progress when server snapshots are slightly stale.
 * This prevents completed/delivered cards from reappearing while hot_stream catches up.
 */
function mergeOrderFulfillmentProgress(serverOrder: TableOrder, localOrder: TableOrder): FulfillmentInfo | undefined {
  const serverFulfillment = serverOrder.fulfillment
  const localFulfillment = localOrder.fulfillment
  if (!serverFulfillment) return localFulfillment
  if (!localFulfillment) return serverFulfillment

  const serverStage = deriveFulfillmentStage(serverOrder)
  const localStage = deriveFulfillmentStage(localOrder)
  const serverRank = FULFILLMENT_STAGE_RANK[serverStage] ?? 0
  const localRank = FULFILLMENT_STAGE_RANK[localStage] ?? 0

  // Server is already ahead (or equal with richer timestamps) — keep it.
  if (localRank < serverRank) return serverFulfillment

  const merged: FulfillmentInfo = { ...serverFulfillment }
  const stageStampFields: Array<
    'placed_at' | 'accepted_at' | 'ready_at' | 'handed_off_at' | 'completed_at'
  > = [
    'placed_at',
    'accepted_at',
    'ready_at',
    'handed_off_at',
    'completed_at',
  ]
  for (const field of stageStampFields) {
    const localValue = localFulfillment[field]
    const serverValue = merged[field]
    if (!serverValue && localValue) {
      merged[field] = localValue
    }
  }
  return merged
}

function mergeDuplicateOrderEntries(a: TableOrder, b: TableOrder): TableOrder {
  const aFresh = orderFreshnessMs(a)
  const bFresh = orderFreshnessMs(b)
  const preferred = (Number.isFinite(bFresh) && (!Number.isFinite(aFresh) || bFresh > aFresh)) ? b : a
  const fallback = preferred === a ? b : a

  const ticketById = new Map<string, Ticket>()
  for (const ticket of [...a.tickets, ...b.tickets]) {
    const existing = ticketById.get(ticket.id_ticket)
    if (!existing) {
      ticketById.set(ticket.id_ticket, ticket)
      continue
    }
    const existingRank = TICKET_STATUS_RANK[existing.status] ?? -1
    const currentRank = TICKET_STATUS_RANK[ticket.status] ?? -1
    if (currentRank > existingRank) {
      ticketById.set(ticket.id_ticket, ticket)
      continue
    }
    if (currentRank < existingRank) continue

    const existingMs = parseIsoMs(existing.updated_at || existing.created_at)
    const currentMs = parseIsoMs(ticket.updated_at || ticket.created_at)
    if (Number.isFinite(currentMs) && (!Number.isFinite(existingMs) || currentMs > existingMs)) {
      ticketById.set(ticket.id_ticket, ticket)
    }
  }

  const tickets = Array.from(ticketById.values()).sort((x, y) => {
    const xMs = parseIsoMs(x.updated_at || x.created_at)
    const yMs = parseIsoMs(y.updated_at || y.created_at)
    if (!Number.isFinite(xMs) && !Number.isFinite(yMs)) return 0
    if (!Number.isFinite(xMs)) return 1
    if (!Number.isFinite(yMs)) return -1
    return yMs - xMs
  })

  return {
    ...preferred,
    fulfillment: preferred.fulfillment ?? fallback.fulfillment,
    tickets,
    status: deriveOrderStatus(tickets),
  }
}

function dedupeOrdersById(orders: TableOrder[]): TableOrder[] {
  const byId = new Map<string, TableOrder>()
  for (const order of orders) {
    const existing = byId.get(order.id_order)
    byId.set(order.id_order, existing ? mergeDuplicateOrderEntries(existing, order) : order)
  }
  return Array.from(byId.values())
}

function dedupeOrdersBySignature(orders: TableOrder[]): TableOrder[] {
  const bySig = new Map<string, TableOrder>()
  for (const order of orders) {
    const sig = orderSignature(order)
    const existing = bySig.get(sig)
    if (!existing) {
      bySig.set(sig, order)
      continue
    }
    const existingMs = orderFreshnessMs(existing)
    const currentMs = orderFreshnessMs(order)
    if (Number.isFinite(currentMs) && (!Number.isFinite(existingMs) || currentMs > existingMs)) {
      bySig.set(sig, mergeDuplicateOrderEntries(existing, order))
    }
  }
  return Array.from(bySig.values())
}

/**
 * Window (ms) for which a locally-placed operation the server has not yet
 * ingested is preserved optimistically. Covers eventual-consistency lag in the
 * hot_stream → reconstruction path without indefinitely retaining orphans.
 */
const REMOTE_OPS_OPTIMISTIC_GRACE_MS = 60_000

/**
 * Server-authoritative merge of a reconstructed operational snapshot over the
 * current local state.
 *
 *  • Server wins for every dine-in scope (deterministic ids).
 *  • Local optimistic entries (newer than generated_at − grace, no server twin
 *    by content signature) are preserved so freshly-placed orders/payments do
 *    not flicker while the hot_stream catches up.
 *  • Locally-advanced ticket statuses (optimistic bumps) are kept when more
 *    advanced than the server's.
 *  • Fulfillment scopes (TA-/DL-) are client-local and pass through untouched.
 *  • closedTables are unioned by session_key (server wins on conflict).
 *
 * Pure function — exercised directly via the APPLY_REMOTE_OPS reducer case.
 */
function mergeRemoteOps(current: OrderState, snapshot: OperationsSnapshot): OrderState {
  const registerOpen = current.register?.status === 'open'
  const activeShiftOpenedMs = (() => {
    if (!registerOpen) return NaN
    const serverOpened = parseIsoMs(snapshot.register?.opened_at)
    const localOpened = parseIsoMs(current.register?.opened_at)
    if (Number.isFinite(serverOpened)) return serverOpened
    if (Number.isFinite(localOpened)) return localOpened
    return NaN
  })()
  const generatedMs = Date.parse(snapshot.generated_at)
  const cutoff = (Number.isFinite(generatedMs) ? generatedMs : Date.now()) - REMOTE_OPS_OPTIMISTIC_GRACE_MS
  const serverRecentClosedSessionKeys = new Set(
    snapshot.closedTables
      .filter((rec) => {
        if (!rec.session_key) return false
        const closedMs = Date.parse(rec.closed_at || '')
        return Number.isFinite(closedMs) && closedMs >= cutoff
      })
      .map((rec) => rec.session_key),
  )

  // Close-side ingestion-lag guard: when the client closes a table, CLOSE_TABLE
  // removes the local session and appends a close record (with session_key) to
  // closedTables. The server snapshot, reconstructed before that close lands in
  // the hot_stream, can still echo the *just-closed* session as open. We must
  // honor the local close and NOT resurrect that table. This is keyed by
  // session_key so a legitimate re-open (new session_key on the same table) is
  // never suppressed.
  const recentLocalClosedSessionKeys = new Set(
    current.closedTables
      .filter((rec) => {
        if (!rec.session_key) return false
        const closedMs = Date.parse(rec.closed_at || '')
        return Number.isFinite(closedMs) && closedMs >= cutoff
      })
      .map((rec) => rec.session_key),
  )
  // Scopes whose server-reported session is one we just closed locally.
  const locallyClosedScopes = new Set<string>()
  for (const s of snapshot.sessions) {
    if (recentLocalClosedSessionKeys.has(s.session_key)) locallyClosedScopes.add(s.table_key)
  }
  // An order belongs to a just-closed session when its id is prefixed by that
  // session_key (id_order = `${session_key}--ord-…::c${i}`).
  const belongsToRecentLocalClose = (idOrder: string): boolean => {
    for (const key of Array.from(recentLocalClosedSessionKeys)) {
      if (idOrder.startsWith(`${key}--`)) return true
    }
    return false
  }
  const belongsToRecentServerClose = (idOrder: string): boolean => {
    for (const key of Array.from(serverRecentClosedSessionKeys)) {
      if (idOrder.startsWith(`${key}--`)) return true
    }
    return false
  }


  // ── Index server slices by dine-in scope ──────────────────────────
  const serverOrdersByScope = new Map<string, TableOrder[]>()
  for (const o of snapshot.orders) {
    if (!registerOpen && isFulfillmentScope(o.id_scope)) continue
    const bucket = serverOrdersByScope.get(o.id_scope) ?? []
    bucket.push(o)
    serverOrdersByScope.set(o.id_scope, bucket)
  }
  const serverSessionsByScope = new Map<string, TableSession>()
  for (const s of snapshot.sessions) {
    if (!registerOpen && isFulfillmentScope(s.table_key)) continue
    if (
      registerOpen &&
      !isFulfillmentScope(s.table_key) &&
      Number.isFinite(activeShiftOpenedMs)
    ) {
      const openedMs = parseIsoMs(s.opened_at)
      if (!Number.isFinite(openedMs) || openedMs < activeShiftOpenedMs) continue
    }
    serverSessionsByScope.set(s.table_key, s)
  }
  const activeSessionKeys = new Set(
    Array.from(serverSessionsByScope.values()).map((s) => s.session_key),
  )
  const serverPaymentsByScope = new Map<string, CheckPayment[]>()
  for (const p of snapshot.payments) {
    if (!registerOpen && isFulfillmentScope(p.scopeId)) continue
    if (
      registerOpen &&
      isFulfillmentScope(p.scopeId) &&
      Number.isFinite(activeShiftOpenedMs)
    ) {
      const paidMs = parseIsoMs(p.paid_at)
      if (!Number.isFinite(paidMs) || paidMs < activeShiftOpenedMs) continue
    }
    if (
      registerOpen &&
      !isFulfillmentScope(p.scopeId) &&
      Number.isFinite(activeShiftOpenedMs)
    ) {
      const paidMs = parseIsoMs(p.paid_at)
      if (!Number.isFinite(paidMs) || paidMs < activeShiftOpenedMs) continue
    }
    const bucket = serverPaymentsByScope.get(p.scopeId) ?? []
    bucket.push(p)
    serverPaymentsByScope.set(p.scopeId, bucket)
  }

  const fulfillmentOrderInActiveShift = (scopeId: string, order: TableOrder): boolean => {
    if (!registerOpen || !isFulfillmentScope(scopeId) || !Number.isFinite(activeShiftOpenedMs)) return true

    const freshestMs = orderFreshnessMs(order)
    if (Number.isFinite(freshestMs) && freshestMs >= activeShiftOpenedMs) return true

    const scopePayments = serverPaymentsByScope.get(scopeId) ?? []
    const matchedPayment = scopePayments.find((p) => p.checkIndex === (order.checkIndex ?? 0))
    const paidMs = parseIsoMs(matchedPayment?.paid_at)
    if (Number.isFinite(paidMs) && paidMs >= activeShiftOpenedMs) return true

    return false
  }

  const dineInOrderInActiveShift = (scopeId: string, order: TableOrder): boolean => {
    if (!registerOpen || isFulfillmentScope(scopeId) || !Number.isFinite(activeShiftOpenedMs)) return true

    const freshestMs = orderFreshnessMs(order)
    if (Number.isFinite(freshestMs) && freshestMs >= activeShiftOpenedMs) return true

    const sessionKey = orderSessionKey(order.id_order)
    if (sessionKey && activeSessionKeys.has(sessionKey)) return true

    const scopePayments = serverPaymentsByScope.get(scopeId) ?? []
    const matchedPayment = scopePayments.find((p) => p.checkIndex === (order.checkIndex ?? 0))
    const paidMs = parseIsoMs(matchedPayment?.paid_at)
    if (Number.isFinite(paidMs) && paidMs >= activeShiftOpenedMs) return true

    return false
  }

  // Universe of dine-in scopes to reconcile (server ∪ local non-fulfillment).
  const dineInScopes = new Set<string>()
  serverOrdersByScope.forEach((_orders, k) => { dineInScopes.add(k) })
  serverSessionsByScope.forEach((_session, k) => { dineInScopes.add(k) })
  serverPaymentsByScope.forEach((_payments, k) => { dineInScopes.add(k) })
  for (const k of Object.keys(current.tables)) if (!isFulfillmentScope(k)) dineInScopes.add(k)
  for (const k of Object.keys(current.sessions)) if (!isFulfillmentScope(k)) dineInScopes.add(k)
  for (const k of Object.keys(current.payments)) if (!isFulfillmentScope(k)) dineInScopes.add(k)

  // ── Rebuild tables ────────────────────────────────────────────────
  const nextTables: Record<string, TableOrder[]> = {}
  // Preserve fulfillment scopes verbatim only while a register is open.
  for (const [scopeId, orders] of Object.entries(current.tables)) {
    if (registerOpen && isFulfillmentScope(scopeId)) nextTables[scopeId] = orders
  }
  for (const scopeId of Array.from(dineInScopes)) {
    const localSession = current.sessions[scopeId]
    const localSessionRecentlyClosed = !!localSession && (
      recentLocalClosedSessionKeys.has(localSession.session_key)
      || serverRecentClosedSessionKeys.has(localSession.session_key)
    )
    const serverOrders = dedupeOrdersById((serverOrdersByScope.get(scopeId) ?? [])
      .filter((o) => fulfillmentOrderInActiveShift(scopeId, o))
      .filter((o) => dineInOrderInActiveShift(scopeId, o))
      .filter((o) => !belongsToRecentLocalClose(o.id_order))
      .filter((o) => !belongsToRecentServerClose(o.id_order)))
    const serverSigs = new Set(serverOrders.map(orderSignature))
    const localOrders = dedupeOrdersById(current.tables[scopeId] ?? [])
    const localBySig = new Map<string, TableOrder>()
    for (const lo of localOrders) localBySig.set(orderSignature(lo), lo)

    // Server orders, with local optimistic ticket advancement merged in.
    const merged: TableOrder[] = serverOrders.map(so => {
      const lo = localBySig.get(orderSignature(so))
      if (!lo) return so
      const { tickets, changed } = mergeOrderTickets(so.tickets, lo.tickets)
      const fulfillment = mergeOrderFulfillmentProgress(so, lo)
      const fulfillmentChanged = fulfillment !== so.fulfillment
      if (!changed && !fulfillmentChanged) return so
      const status = fulfillment?.completed_at ? 'closed' : deriveOrderStatus(tickets)
      return { ...so, tickets, fulfillment, status }
    })

    // Local optimistic orders with no server twin yet (within grace window).
    // Use freshness (created/updated/ticket bumps) instead of created_at only,
    // so optimistic status transitions do not flicker on transient lag.
    // A recent server close for this scope is authoritative and must not be
    // masked by local optimistic residue.
    if (!localSessionRecentlyClosed && !locallyClosedScopes.has(scopeId)) {
      for (const lo of localOrders) {
        if (serverSigs.has(orderSignature(lo))) continue
        const freshMs = orderFreshnessMs(lo)
        if (Number.isFinite(freshMs) && freshMs >= cutoff) merged.unshift(lo)
      }
    }

    // If server currently has no orders for the scope and did not explicitly
    // mark it as closed, keep local scope orders when that scope is still fresh.
    if (serverOrders.length === 0 && merged.length === 0 && !localSessionRecentlyClosed && !locallyClosedScopes.has(scopeId)) {
      const localFreshMs = scopeFreshnessMs(current, scopeId)
      if (Number.isFinite(localFreshMs) && localFreshMs >= cutoff && localOrders.length > 0) {
        nextTables[scopeId] = dedupeOrdersBySignature(localOrders)
        continue
      }
    }

    if (merged.length > 0) {
      const normalized = dedupeOrdersBySignature(dedupeOrdersById(merged))
      normalized.sort((a, b) => {
        const aMs = orderFreshnessMs(a)
        const bMs = orderFreshnessMs(b)
        if (!Number.isFinite(aMs) && !Number.isFinite(bMs)) return 0
        if (!Number.isFinite(aMs)) return 1
        if (!Number.isFinite(bMs)) return -1
        return bMs - aMs
      })
      nextTables[scopeId] = normalized
    }
  }

  // ── Rebuild sessions ──────────────────────────────────────────────
  const nextSessions: Record<string, TableSession> = {}
  for (const [scopeId, session] of Object.entries(current.sessions)) {
    if (registerOpen && isFulfillmentScope(scopeId)) nextSessions[scopeId] = session
  }
  for (const scopeId of Array.from(dineInScopes)) {
    const serverSession = serverSessionsByScope.get(scopeId)
    const localSession = current.sessions[scopeId]
    // Close-side ingestion lag: the server is still echoing a session the
    // client just closed locally. Honor the local close — do not resurrect it.
    const serverEchoesLocalClose = !!serverSession && recentLocalClosedSessionKeys.has(serverSession.session_key)
    if (serverSession && !serverEchoesLocalClose) {
      // Ingestion-lag guard: a just-opened local session (newer opened_at)
      // must not be rewound to a stale server session that has not yet caught
      // up. Keep the locally-opened session until the server reports one at
      // least as new, unless the scope was explicitly closed recently.
      if (
        localSession &&
        !recentLocalClosedSessionKeys.has(localSession.session_key) &&
        !serverRecentClosedSessionKeys.has(localSession.session_key) &&
        localSession.session_key !== serverSession.session_key
      ) {
        const localOpenedMs = parseIsoMs(localSession.opened_at)
        const serverOpenedMs = parseIsoMs(serverSession.opened_at)
        if (
          Number.isFinite(localOpenedMs) &&
          localOpenedMs >= cutoff &&
          (!Number.isFinite(serverOpenedMs) || localOpenedMs > serverOpenedMs)
        ) {
          nextSessions[scopeId] = localSession
          continue
        }
      }
      nextSessions[scopeId] = serverSession
      continue
    }
    // Either no server session, or the server is echoing a just-closed one.
    // Keep a fresh local session only if it is not itself a closed scope and
    // not the very session we just closed (re-open keeps a new session_key).
    if (
      localSession &&
      !recentLocalClosedSessionKeys.has(localSession.session_key) &&
      !serverRecentClosedSessionKeys.has(localSession.session_key)
    ) {
      const freshMs = scopeFreshnessMs(current, scopeId)
      if (Number.isFinite(freshMs) && freshMs >= cutoff) nextSessions[scopeId] = localSession
    }
  }

  // ── Rebuild payments ──────────────────────────────────────────────
  const nextPayments: Record<string, CheckPayment[]> = {}
  for (const [scopeId, pays] of Object.entries(current.payments)) {
    if (registerOpen && isFulfillmentScope(scopeId)) nextPayments[scopeId] = pays
  }
  const paymentSig = (p: CheckPayment) => `${p.checkIndex}|${p.paymentMethodId}|${p.amount}|${p.tip}|${p.paid_at}`
  for (const scopeId of Array.from(dineInScopes)) {
    const localSession = current.sessions[scopeId]
    const localSessionRecentlyClosed = !!localSession && (
      recentLocalClosedSessionKeys.has(localSession.session_key)
      || serverRecentClosedSessionKeys.has(localSession.session_key)
    )
    const serverPays = serverPaymentsByScope.get(scopeId) ?? []
    const serverSigs = new Set(serverPays.map(paymentSig))
    const merged: CheckPayment[] = (localSessionRecentlyClosed || locallyClosedScopes.has(scopeId)) ? [] : [...serverPays]
    if (!localSessionRecentlyClosed && !locallyClosedScopes.has(scopeId)) {
      for (const lp of current.payments[scopeId] ?? []) {
        if (serverSigs.has(paymentSig(lp))) continue
        const paidMs = Date.parse(lp.paid_at)
        if (Number.isFinite(paidMs) && paidMs >= cutoff) merged.push(lp)
      }
    }
    if (serverPays.length === 0 && merged.length === 0 && !localSessionRecentlyClosed && !locallyClosedScopes.has(scopeId)) {
      const localFreshMs = scopeFreshnessMs(current, scopeId)
      if (Number.isFinite(localFreshMs) && localFreshMs >= cutoff) {
        const localPays = current.payments[scopeId] ?? []
        if (localPays.length > 0) {
          nextPayments[scopeId] = localPays
          continue
        }
      }
    }
    if (merged.length > 0) nextPayments[scopeId] = merged
  }

  // ── checksPerScope from server sessions (preserve local otherwise) ─
  const nextChecks: Record<string, number> = { ...current.checksPerScope }
  for (const s of Array.from(serverSessionsByScope.values())) {
    if (Number.isFinite(s.checks) && s.checks > 0) nextChecks[s.table_key] = s.checks
  }

  // ── closedTables union by session_key (server wins) ───────────────
  const closedByKey = new Map<string, TableCloseRecord>()
  for (const rec of current.closedTables) closedByKey.set(rec.session_key, rec)
  for (const rec of snapshot.closedTables) closedByKey.set(rec.session_key, rec)
  const nextClosed = Array.from(closedByKey.values())

  // ── Register shift (server-authoritative, converges across operators) ──
  // The open cashier shift is reconstructed from hot_stream on the server, so
  // it survives reloads and is identical for every operator on the tenant.
  // Two ingestion-lag guards mirror the session model:
  //   • close-lag — a shift we just closed locally (within grace) must not be
  //     resurrected if the snapshot was reconstructed before the close landed.
  //   • open-lag  — a shift we just opened locally (within grace) must not be
  //     rewound to "no register" if the open event has not landed yet.
  const recentlyClosedShiftIds = new Set(
    current.closedShifts
      .filter((s) => {
        const closedMs = parseIsoMs(s.closed_at || '')
        return Number.isFinite(closedMs) && closedMs >= cutoff
      })
      .map((s) => s.id),
  )
  const serverRegister = snapshot.register ?? null
  let nextRegister: RegisterShift | null
  if (serverRegister && !recentlyClosedShiftIds.has(serverRegister.id)) {
    // Server reports an open shift we have not just closed — adopt it as truth,
    // preserving any locally-tracked closedTables snapshot for the same shift.
    nextRegister =
      current.register && current.register.id === serverRegister.id
        ? { ...serverRegister, closedTables: current.register.closedTables }
        : serverRegister
  } else if (current.register && current.register.status === 'open') {
    // No authoritative server shift (or it was just closed locally). Keep a
    // freshly-opened local shift within the grace window (open-lag); otherwise
    // the server is authoritative that no register is open.
    const openedMs = parseIsoMs(current.register.opened_at)
    nextRegister = Number.isFinite(openedMs) && openedMs >= cutoff ? current.register : null
  } else {
    nextRegister = null
  }

  return {
    ...current,
    tables: nextTables,
    sessions: nextSessions,
    payments: nextPayments,
    checksPerScope: nextChecks,
    closedTables: nextClosed,
    register: nextRegister,
  }
}

/* ── Actions ────────────────────────────────────────────────────────── */

/** Generate shift ID as yymmddnn (nn = daily sequence 00,01,02…) */
function generateShiftId(closedShifts: RegisterShift[]): string {
  const now = new Date()
  const yy = String(now.getFullYear()).slice(-2)
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const prefix = `${yy}${mm}${dd}`
  const todayCount = closedShifts.filter(s => s.id.startsWith(prefix)).length
  const nn = String(todayCount).padStart(2, '0')
  return `${prefix}${nn}`
}

type OrderAction =
  | {
      type: 'DISPATCH'
      payload: {
        scopeId: string
        checkIndex: number
        lines: Array<{
          id: number
          scopeKey?: string
          name: string
          description: string
          price: number
          quantity: number
          section: string
          station?: StationId
          note?: string
        }>
        /** Optional fulfillment block — absent = dine-in. */
        fulfillment?: FulfillmentInfo
      }
    }
  | {
      /**
       * Stamp a fulfillment-stage timestamp on a takeaway/delivery order.
       * Idempotent: re-stamping a stage that already has a timestamp is a no-op.
       * When stage = 'completed', the order's top-level status is also set to 'closed'.
       */
      type: 'MARK_FULFILLMENT_STAGE'
      payload: { orderId: string; stage: FulfillmentStage; at?: string }
    }
  | {
      type: 'BUMP_TICKET'
      payload: { orderId: string; ticketId: string }
    }
  | {
      /** Mark all ready tickets on an order as delivered together. */
      type: 'DELIVER_ORDER'
      payload: { orderId: string }
    }
  | {
      /** Void a ticket — sets status to 'discarded', shown in dark red on the orders table */
      type: 'DISCARD_TICKET'
      payload: { orderId: string; ticketId: string }
    }
  | {
      /** Remove one specific line from a ticket while preserving the rest of the ticket/order. */
      type: 'REMOVE_TICKET_LINE'
      payload: { orderId: string; ticketId: string; lineIndex: number }
    }
  | {
      type: 'SET_SCOPE'
      payload: { scopeId: string }
    }
  | {
      type: 'CLEAR_CLOSED'
      payload: { scopeId: string }
    }
  | {
      /** Set how many checks (split bills) a table should have */
      type: 'SET_CHECKS'
      payload: { scopeId: string; count: number }
    }
  | {
      /** Switch the active check tab for a scope */
      type: 'SET_ACTIVE_CHECK'
      payload: { scopeId: string; checkIndex: number }
    }
  | {
      /** Move an existing order line from one check to another */
      type: 'MOVE_TO_CHECK'
      payload: { scopeId: string; orderId: string; targetCheck: number }
    }
  | {
      /** Store a newly opened hot table session */
      type: 'OPEN_SESSION'
      payload: TableSession
    }
  | {
      /** Store session when table is closed */
      type: 'CLOSE_SESSION'
      payload: { scopeId: string }
    }
  | {
      /** Sync the full merge list from the floor-edit reducer */
      type: 'SET_MERGES'
      payload: MergeRecord[]
    }
  | {
      /** Open a new register shift without mutating live table state. */
      type: 'OPEN_REGISTER'
      payload: { openingFloat: number; shiftId?: string; registerNumber?: number; openedAt?: string }
    }
  | {
      /** Close the current register shift — saves ledger snapshot, keeps live state server-authoritative. */
      type: 'CLOSE_REGISTER'
    }
  | {
      /** Roll back an optimistic open whose server persistence failed (no ledger entry recorded). */
      type: 'ROLLBACK_REGISTER'
      payload: { shiftId: string }
    }
  | {
      /** Overwrite payment methods list */
      type: 'SET_PAYMENT_METHODS'
      payload: PaymentMethod[]
    }
  | {
      /** Toggle a payment method's enabled flag */
      type: 'TOGGLE_PAYMENT_METHOD'
      payload: { id: string }
    }
  | {
      /** Bulk set register configs (scope family) */
      type: 'SET_REGISTER_CONFIGS'
      payload: RegisterConfig[]
    }
  | {
      /** Add a new register to the scope family */
      type: 'ADD_REGISTER_CONFIG'
    }
  | {
      /** Switch active register number */
      type: 'SET_ACTIVE_REGISTER_NUM'
      payload: { number: number }
    }
  | {
      /** Delete a register from the scope family */
      type: 'DELETE_REGISTER_CONFIG'
      payload: { number: number }
    }
  | {
      /** Link payment methods to a specific register */
      type: 'LINK_REGISTER_METHODS'
      payload: { register_number: number; payment_method_ids: string[] }
    }
  | {
      /** Record a payment for a specific check */
      type: 'PAY_CHECK'
      payload: CheckPayment
    }
  | {
      /** Roll back one optimistic payment when backend persistence fails. */
      type: 'REMOVE_CHECK_PAYMENT'
      payload: { scopeId: string; checkIndex: number; paid_at: string }
    }
  | {
      /** Close a table — record final summary, remove session, clear orders */
      type: 'CLOSE_TABLE'
      payload: { scopeId: string }
    }
  | {
      /** Reset all state (clears localStorage too) */
      type: 'RESET_STATE'
    }
  | {
      /** Replace entire state (used when switching tenant) */
      type: 'REPLACE_STATE'
      payload: OrderState
    }
  | {
      /** Bulk-advance all tickets for a scope from one status to another (used by sim bridge for order_ready / order_delivered) */
      type: 'BUMP_TABLE_TICKETS'
      payload: { scopeId: string; fromStatus: TicketStatus; toStatus: TicketStatus }
    }
  | {
      /** Replace orders for a specific table — used by sim bridge to hydrate orders from ring buffer on mount */
      type: 'HYDRATE_TABLE_ORDERS'
      payload: { scopeId: string; orders: TableOrder[] }
    }
  | {
      /** Remove an entire scope after an optimistic write failed durability. */
      type: 'ROLLBACK_SCOPE'
      payload: { scopeId: string }
    }
  | {
      /**
       * Merge a server-reconstructed operational snapshot over local state.
       * Server-authoritative per dine-in scope; local optimistic entries and
       * fulfillment scopes are preserved. Drives cross-user live sync.
       */
      type: 'APPLY_REMOTE_OPS'
      payload: OperationsSnapshot
    }

/* ── Reducer ────────────────────────────────────────────────────────── */

/** Exported for direct unit-testing — use useOrderStore() in components. */
export { INITIAL_STATE as ORDER_INITIAL_STATE }
export type { OrderState, OrderAction }

export function orderReducer(state: OrderState, action: OrderAction): OrderState {
  switch (action.type) {
    case 'DISPATCH': {
      const { scopeId, checkIndex, lines, fulfillment } = action.payload
      if (lines.length === 0) return state
      const order = dispatchOrder(scopeId, lines, checkIndex, fulfillment)
      const existing = state.tables[scopeId] ?? []
      return {
        ...state,
        tables: {
          ...state.tables,
          [scopeId]: [order, ...existing],
        },
      }
    }

    case 'MARK_FULFILLMENT_STAGE': {
      const { orderId, stage, at } = action.payload
      const tables = { ...state.tables }

      for (const scope of Object.keys(tables)) {
        const orders = tables[scope]
        const oi = orders.findIndex(o => o.id_order === orderId)
        if (oi < 0) continue
        const order = orders[oi]
        if (!order.fulfillment) continue // not a fulfillment order — ignore

        const ts = at ?? new Date().toISOString()
        const nextFulfillment = stampFulfillmentStage(order.fulfillment, stage, at)
        // Keep KDS + fulfillment in lockstep: Marking an order as 'ready'
        // from the fulfillment panel is semantically equivalent to pressing
        // DONE for the remaining preparing tickets in KDS.
        let ticketsChanged = false
        let nextTickets = order.tickets
        if (stage === 'ready') {
          nextTickets = order.tickets.map((t: Ticket) => {
            if (t.status === 'open' || t.status === 'in_progress') {
              ticketsChanged = true
              return {
                ...t,
                status: 'ready',
                updated_at: ts,
                bumped_at: t.bumped_at ?? ts,
              }
            }
            return t
          })
        } else if (stage === 'completed') {
          nextTickets = order.tickets.map((t: Ticket) => {
            if (t.status === 'open' || t.status === 'in_progress' || t.status === 'ready') {
              ticketsChanged = true
              return {
                ...t,
                status: 'delivered',
                updated_at: ts,
                bumped_at: t.bumped_at ?? ts,
              }
            }
            return t
          })
        }

        // No-op (timestamp already set + no ticket sync delta)
        if (nextFulfillment === order.fulfillment && !ticketsChanged) return state

        const updated: TableOrder = {
          ...order,
          fulfillment: nextFulfillment,
          tickets: nextTickets,
          updated_at: ts,
          status: stage === 'completed'
            ? 'closed'
            : deriveOrderStatus(nextTickets),
        }
        const newOrders = [...orders]
        newOrders[oi] = updated
        tables[scope] = newOrders
        break
      }

      return { ...state, tables }
    }

    case 'BUMP_TICKET': {
      const { orderId, ticketId } = action.payload
      const tables = { ...state.tables }

      for (const scope of Object.keys(tables)) {
        const orders = tables[scope]
        const oi = orders.findIndex(o => o.id_order === orderId)
        if (oi < 0) continue

        const order = { ...orders[oi] }
        const ti = order.tickets.findIndex((t: Ticket) => t.id_ticket === ticketId)
        if (ti < 0) continue

        const ticket = { ...order.tickets[ti] }
        const next = nextTicketStatus(ticket.status)
        if (!next) continue

        ticket.status = next
        ticket.updated_at = new Date().toISOString()
        if (next === 'ready') ticket.bumped_at = ticket.updated_at

        const tickets = [...order.tickets]
        tickets[ti] = ticket
        order.tickets = tickets
        order.status = deriveOrderStatus(tickets)
        order.updated_at = ticket.updated_at

        const newOrders = [...orders]
        newOrders[oi] = order
        tables[scope] = newOrders
        break
      }

      return { ...state, tables }
    }

    case 'DELIVER_ORDER': {
      const { orderId } = action.payload
      const tables = { ...state.tables }

      for (const scope of Object.keys(tables)) {
        const orders = tables[scope]
        const oi = orders.findIndex(o => o.id_order === orderId)
        if (oi < 0) continue

        const order = { ...orders[oi] }
        const ts = new Date().toISOString()
        let changed = false
        const tickets = order.tickets.map((t: Ticket) => {
          if (t.status !== 'ready') return t
          changed = true
          return {
            ...t,
            status: 'delivered' as TicketStatus,
            updated_at: ts,
          }
        })

        if (!changed) return state

        order.tickets = tickets
        order.status = deriveOrderStatus(tickets)
        order.updated_at = ts

        const newOrders = [...orders]
        newOrders[oi] = order
        tables[scope] = newOrders
        break
      }

      return { ...state, tables }
    }

    case 'DISCARD_TICKET': {
      const { orderId, ticketId } = action.payload
      const tables = { ...state.tables }

      for (const scope of Object.keys(tables)) {
        const orders = tables[scope]
        const oi = orders.findIndex(o => o.id_order === orderId)
        if (oi < 0) continue

        const order = { ...orders[oi] }
        const ti = order.tickets.findIndex((t: Ticket) => t.id_ticket === ticketId)
        if (ti < 0) continue

        const ticket = { ...order.tickets[ti], status: 'discarded' as TicketStatus, updated_at: new Date().toISOString() }
        const tickets = [...order.tickets]
        tickets[ti] = ticket
        order.tickets = tickets
        order.updated_at = ticket.updated_at

        const newOrders = [...orders]
        newOrders[oi] = order
        tables[scope] = newOrders
        break
      }

      return { ...state, tables }
    }

    case 'REMOVE_TICKET_LINE': {
      const { orderId, ticketId, lineIndex } = action.payload
      const tables = { ...state.tables }

      for (const scope of Object.keys(tables)) {
        const orders = tables[scope]
        const oi = orders.findIndex(o => o.id_order === orderId)
        if (oi < 0) continue

        const order = { ...orders[oi] }
        const ti = order.tickets.findIndex((t: Ticket) => t.id_ticket === ticketId)
        if (ti < 0) continue

        const ticket = order.tickets[ti]
        if (lineIndex < 0 || lineIndex >= ticket.lines.length) return state

        const nextLines = ticket.lines.filter((_, idx) => idx !== lineIndex)
        const nowIso = new Date().toISOString()
        const nextTickets = [...order.tickets]

        if (nextLines.length === 0) {
          nextTickets.splice(ti, 1)
        } else {
          nextTickets[ti] = {
            ...ticket,
            lines: nextLines,
            updated_at: nowIso,
          }
        }

        const nextTotal = nextTickets.reduce((sum, tk) => (
          sum + tk.lines.reduce((lineSum, line) => lineSum + (line.price * line.quantity), 0)
        ), 0)

        const nextOrder: TableOrder = {
          ...order,
          tickets: nextTickets,
          total: nextTotal,
          updated_at: nowIso,
          status: deriveOrderStatus(nextTickets),
        }

        const newOrders = [...orders]
        newOrders[oi] = nextOrder
        tables[scope] = newOrders
        break
      }

      return { ...state, tables }
    }

    case 'SET_SCOPE':
      return { ...state, activeScope: action.payload.scopeId }

    case 'SET_CHECKS': {
      const { scopeId, count } = action.payload
      return {
        ...state,
        checksPerScope: { ...state.checksPerScope, [scopeId]: Math.max(1, count) },
        activeCheck: {
          ...state.activeCheck,
          // clamp active check within new range
          [scopeId]: Math.min(state.activeCheck[scopeId] ?? 0, Math.max(0, count - 1)),
        },
      }
    }

    case 'SET_ACTIVE_CHECK': {
      const { scopeId, checkIndex } = action.payload
      return {
        ...state,
        activeCheck: { ...state.activeCheck, [scopeId]: checkIndex },
      }
    }

    case 'MOVE_TO_CHECK': {
      const { scopeId, orderId, targetCheck } = action.payload
      const orders = state.tables[scopeId]
      if (!orders) return state
      const idx = orders.findIndex(o => o.id_order === orderId)
      if (idx < 0) return state
      const updated = { ...orders[idx], checkIndex: targetCheck, updated_at: new Date().toISOString() }
      const newOrders = [...orders]
      newOrders[idx] = updated
      return {
        ...state,
        tables: { ...state.tables, [scopeId]: newOrders },
      }
    }

    case 'CLEAR_CLOSED': {
      const { scopeId } = action.payload
      const orders = state.tables[scopeId]
      if (!orders) return state
      return {
        ...state,
        tables: {
          ...state.tables,
          [scopeId]: orders.filter(o => o.status !== 'closed'),
        },
      }
    }

    case 'OPEN_SESSION': {
      const session = action.payload
      return {
        ...state,
        sessions: { ...state.sessions, [session.table_key]: session },
      }
    }

    case 'CLOSE_SESSION': {
      const { scopeId } = action.payload
      const { [scopeId]: _, ...rest } = state.sessions
      return { ...state, sessions: rest }
    }

    case 'SET_MERGES': {
      return { ...state, merges: action.payload }
    }

    case 'OPEN_REGISTER': {
      // Idempotent — if a register is already open, don't replace it
      // or disturb the server-authoritative live table/session state.
      if (state.register?.status === 'open') return state

      const { openingFloat, shiftId, registerNumber, openedAt } = action.payload
      const shift: RegisterShift = {
        id: shiftId || generateShiftId(state.closedShifts),
        register_number: registerNumber ?? state.activeRegisterNum,
        opened_at: openedAt || new Date().toISOString(),
        opening_float: openingFloat,
        status: 'open',
      }
      return {
        ...state,
        register: shift,
      }
    }

    case 'CLOSE_REGISTER': {
      if (!state.register) return state
      const shiftOpenedMs = parseIsoMs(state.register.opened_at)
      const shiftClosedTables = state.closedTables.filter((record) => {
        const closedMs = parseIsoMs(record.closed_at)
        if (!Number.isFinite(shiftOpenedMs)) return true
        return Number.isFinite(closedMs) && closedMs >= shiftOpenedMs
      })

      // Local optimistic fulfillment snapshot so cashflow history card reflects
      // TA/DL income immediately while server projection catches up.
      let takeawayCount = 0
      let deliveryCount = 0
      let takeawayRevenue = 0
      let deliveryRevenue = 0
      let fulfillmentTips = 0

      for (const [scopeId, orders] of Object.entries(state.tables)) {
        const kind = /^DL-/i.test(scopeId) ? 'delivery' : (/^TA-/i.test(scopeId) ? 'takeaway' : null)
        if (!kind) continue

        for (const order of orders) {
          if (!order.fulfillment) continue
          const payment = (state.payments[scopeId] ?? []).find((p) => p.checkIndex === 0) ?? null
          const happenedAt = payment?.paid_at ?? order.fulfillment.completed_at ?? null
          const happenedMs = parseIsoMs(happenedAt)
          if (Number.isFinite(shiftOpenedMs) && (!Number.isFinite(happenedMs) || happenedMs < shiftOpenedMs)) {
            continue
          }
          const amount = Number(payment?.amount ?? order.total ?? 0)
          const tip = Number(payment?.tip ?? 0)
          const gross = amount + tip
          if (!Number.isFinite(gross) || gross <= 0) continue

          if (kind === 'takeaway') {
            takeawayCount += 1
            takeawayRevenue += gross
          } else {
            deliveryCount += 1
            deliveryRevenue += gross
          }
          fulfillmentTips += tip
        }
      }

      const closedShift: RegisterShift = {
        ...state.register,
        status: 'closed',
        closed_at: new Date().toISOString(),
        closedTables: shiftClosedTables,
        fulfillment_takeaway_count: takeawayCount,
        fulfillment_delivery_count: deliveryCount,
        fulfillment_takeaway_revenue: takeawayRevenue,
        fulfillment_delivery_revenue: deliveryRevenue,
        fulfillment_total_tips: fulfillmentTips,
      }

      // Shift boundary reset: fulfillment scopes are shift-bound and should not
      // keep appearing in live KDS/fulfillment queues after a close.
      const nextTables: OrderState['tables'] = {}
      const nextPayments: OrderState['payments'] = {}
      const nextSessions: OrderState['sessions'] = {}
      const nextChecksPerScope: OrderState['checksPerScope'] = {}
      const nextActiveCheck: OrderState['activeCheck'] = {}

      for (const [scopeId, orders] of Object.entries(state.tables)) {
        if (isFulfillmentScope(scopeId)) continue
        nextTables[scopeId] = orders
      }
      for (const [scopeId, pays] of Object.entries(state.payments)) {
        if (isFulfillmentScope(scopeId)) continue
        nextPayments[scopeId] = pays
      }
      for (const [scopeId, session] of Object.entries(state.sessions)) {
        if (isFulfillmentScope(scopeId)) continue
        nextSessions[scopeId] = session
      }
      for (const [scopeId, count] of Object.entries(state.checksPerScope)) {
        if (isFulfillmentScope(scopeId)) continue
        nextChecksPerScope[scopeId] = count
      }
      for (const [scopeId, checkIndex] of Object.entries(state.activeCheck)) {
        if (isFulfillmentScope(scopeId)) continue
        nextActiveCheck[scopeId] = checkIndex
      }

      return {
        ...state,
        register: null,
        tables: nextTables,
        payments: nextPayments,
        sessions: nextSessions,
        checksPerScope: nextChecksPerScope,
        activeCheck: nextActiveCheck,
        closedShifts: [...state.closedShifts, closedShift],
      }
    }

    case 'ROLLBACK_REGISTER': {
      // Undo a failed optimistic open. Only clears the register if it still
      // matches the shift we tried to open (a later server-confirmed open or a
      // different shift must NOT be wiped). No closed-shift ledger entry is
      // recorded — the open never reached the server.
      if (!state.register || state.register.id !== action.payload.shiftId) return state
      return { ...state, register: null }
    }

    case 'SET_PAYMENT_METHODS': {
      return { ...state, paymentMethods: action.payload }
    }

    case 'TOGGLE_PAYMENT_METHOD': {
      const { id } = action.payload
      return {
        ...state,
        paymentMethods: state.paymentMethods.map(pm =>
          pm.id === id ? { ...pm, enabled: !pm.enabled } : pm,
        ),
      }
    }

    case 'SET_REGISTER_CONFIGS': {
      const configs = action.payload as RegisterConfig[]
      // If the current activeRegisterNum doesn't exist in the new configs, reset to the first
      const nums = configs.map(r => r.number)
      const activeNum = nums.includes(state.activeRegisterNum)
        ? state.activeRegisterNum
        : (nums[0] ?? 1)
      return { ...state, registerConfigs: configs, activeRegisterNum: activeNum }
    }

    case 'ADD_REGISTER_CONFIG': {
      const nextNum = state.registerConfigs.length > 0
        ? Math.max(...state.registerConfigs.map(r => r.number)) + 1
        : 1
      const config: RegisterConfig = {
        number: nextNum,
        label: `Register ${nextNum}`,
        payment_method_ids: [],
      }
      return { ...state, registerConfigs: [...state.registerConfigs, config] }
    }

    case 'SET_ACTIVE_REGISTER_NUM': {
      return { ...state, activeRegisterNum: action.payload.number }
    }

    case 'DELETE_REGISTER_CONFIG': {
      const { number } = action.payload
      // Don't allow deleting the last register
      if (state.registerConfigs.length <= 1) return state
      // Don't allow deleting if it's the currently open register
      if (state.register && state.register.register_number === number) return state
      const filtered = state.registerConfigs.filter(r => r.number !== number)
      return {
        ...state,
        registerConfigs: filtered,
        // If the deleted register was the active one, switch to first available
        activeRegisterNum: state.activeRegisterNum === number
          ? filtered[0].number
          : state.activeRegisterNum,
      }
    }

    case 'LINK_REGISTER_METHODS': {
      const { register_number, payment_method_ids } = action.payload
      return {
        ...state,
        registerConfigs: state.registerConfigs.map(r =>
          r.number === register_number
            ? { ...r, payment_method_ids }
            : r
        ),
      }
    }

    case 'PAY_CHECK': {
      const payment = action.payload
      const existing = state.payments[payment.scopeId] ?? []
      return {
        ...state,
        payments: { ...state.payments, [payment.scopeId]: [...existing, payment] },
      }
    }

    case 'REMOVE_CHECK_PAYMENT': {
      const { scopeId, checkIndex, paid_at } = action.payload
      const existing = state.payments[scopeId] ?? []
      const next = existing.filter(p => !(p.checkIndex === checkIndex && p.paid_at === paid_at))
      return {
        ...state,
        payments: { ...state.payments, [scopeId]: next },
      }
    }

    case 'CLOSE_TABLE': {
      const { scopeId } = action.payload
      const session = state.sessions[scopeId]
      const orders = state.tables[scopeId] ?? []
      const payments = state.payments[scopeId] ?? []
      const nowIso = new Date().toISOString()
      // Primary: compute from order history.  Fallback: sum payment
      // amounts (covers the shift-boundary edge case where OPEN_REGISTER
      // cleared tables{} before this table_closed event was processed).
      const orderSubtotal = orders.reduce((s, o) => s + o.total, 0)
      const subtotal = orderSubtotal || payments.reduce((s, p) => s + p.amount, 0)
      const total_tips = payments.reduce((s, p) => s + p.tip, 0)

      // Aggregate line items across all orders for analytics (top-N ranking)
      const itemMap = new Map<string, ItemSummary>()
      for (const o of orders) {
        for (const t of o.tickets) {
          for (const ln of t.lines) {
            const key = ln.item_key || ln.name
            const prev = itemMap.get(key)
            if (prev) {
              prev.quantity += ln.quantity
              prev.revenue += ln.price * ln.quantity
            } else {
              itemMap.set(key, {
                item_key: ln.item_key,
                name: ln.name,
                quantity: ln.quantity,
                revenue: ln.price * ln.quantity,
              })
            }
          }
        }
      }

      const record: TableCloseRecord = {
        scopeId,
        session_key: session?.session_key ?? '',
        payments,
        closed_at: nowIso,
        subtotal,
        total_tips,
        grand_total: subtotal + total_tips,
        items: Array.from(itemMap.values()),
      }

      // Preserve the order timeline for KDS/history views even after table close.
      // We freeze remaining non-discarded tickets as `closed` to mark completion.
      const finalizedOrders = orders.map(order => ({
        ...order,
        status: 'closed' as const,
        updated_at: nowIso,
        tickets: order.tickets.map(ticket =>
          ticket.status === 'discarded'
            ? { ...ticket, updated_at: nowIso }
            : { ...ticket, status: 'closed' as TicketStatus, updated_at: nowIso },
        ),
      }))

      // Clean up table state
      const { [scopeId]: _s, ...restSessions } = state.sessions
      const { [scopeId]: _p, ...restPayments } = state.payments
      const { [scopeId]: _c, ...restChecks } = state.checksPerScope
      const { [scopeId]: _a, ...restActive } = state.activeCheck

      return {
        ...state,
        sessions: restSessions,
        tables: { ...state.tables, [scopeId]: finalizedOrders },
        payments: restPayments,
        checksPerScope: restChecks,
        activeCheck: restActive,
        closedTables: [...state.closedTables, record],
      }
    }

    case 'RESET_STATE': {
      return { ...INITIAL_STATE }
    }

    case 'REPLACE_STATE': {
      return action.payload
    }

    case 'BUMP_TABLE_TICKETS': {
      const { scopeId, fromStatus, toStatus } = action.payload
      const orders = state.tables[scopeId]
      if (!orders) return state
      const now = new Date().toISOString()
      let changed = false
      const updated = orders.map(o => {
        let orderChanged = false
        const tickets = o.tickets.map(t => {
          if (t.status !== fromStatus) return t
          changed = true
          orderChanged = true
          return {
            ...t,
            status: toStatus,
            updated_at: now,
            ...(toStatus === 'ready' ? { bumped_at: now } : {}),
          }
        })
        return orderChanged
          ? { ...o, tickets, status: deriveOrderStatus(tickets), updated_at: now }
          : o
      })
      return changed ? { ...state, tables: { ...state.tables, [scopeId]: updated } } : state
    }

    case 'HYDRATE_TABLE_ORDERS': {
      const { scopeId, orders } = action.payload
      if (!orders.length) return state
      return {
        ...state,
        tables: { ...state.tables, [scopeId]: orders },
      }
    }

    case 'ROLLBACK_SCOPE': {
      const { scopeId } = action.payload
      const { [scopeId]: _orders, ...restTables } = state.tables
      const { [scopeId]: _sessions, ...restSessions } = state.sessions
      const { [scopeId]: _payments, ...restPayments } = state.payments
      const { [scopeId]: _checks, ...restChecks } = state.checksPerScope
      const { [scopeId]: _activeCheck, ...restActiveCheck } = state.activeCheck
      return {
        ...state,
        tables: restTables,
        sessions: restSessions,
        payments: restPayments,
        checksPerScope: restChecks,
        activeCheck: restActiveCheck,
      }
    }

    case 'APPLY_REMOTE_OPS': {
      return mergeRemoteOps(state, action.payload)
    }

    default:
      return state
  }
}

/* ── Context ────────────────────────────────────────────────────────── */

interface OrderContextValue {
  state: OrderState
  dispatch: React.Dispatch<OrderAction>
  /** Convenience: dispatch an order for the current scope + active check */
  placeOrder: (
    lines: Array<{
      id: number
      scopeKey?: string
      name: string
      description: string
      price: number
      quantity: number
      section: string
      note?: string
    }>,
  ) => void
  /** Convenience: bump a ticket to the next status */
  bumpTicket: (orderId: string, ticketId: string) => void
  /** Convenience: mark all ready tickets on an order as delivered together */
  markOrderDelivered: (orderId: string) => void
  /** Void / discard a ticket — sets status to 'discarded' */
  discardTicket: (orderId: string, ticketId: string) => void
  /** Remove one line from a ticket without discarding the full ticket. */
  removeTicketLine: (orderId: string, ticketId: string, lineIndex: number) => void
  /**
   * Create a takeaway/delivery order. Uses a synthetic scope id (TA-xxxx / DL-xxxx),
   * routes lines to per-station tickets exactly like dine-in (KDS unchanged), and
   * stamps `placed_at` automatically. Returns the generated scopeId after
   * durability acknowledgement, and rolls back the optimistic row on failure.
   */
  placeFulfillmentOrder: (input: {
    fulfillment: Omit<FulfillmentInfo, 'placed_at'> & { placed_at?: string }
    lines: Array<{
      id: number
      scopeKey?: string
      name: string
      description: string
      price: number
      quantity: number
      section: string
      station?: StationId
      note?: string
    }>
  }) => Promise<{ scopeId: string }>
  /** Persist + record a TA/DL payment (fail-closed: local write only after server ack). */
  payFulfillmentOrder: (input: {
    scopeId: string
    amount: number
    tip: number
    paymentMethodId: string
    fulfillmentType?: 'delivery' | 'takeaway'
    paidAt?: string
  }) => Promise<void>
  /** Stamp + persist a fulfillment lifecycle stage timestamp (idempotent). */
  markFulfillmentStage: (orderId: string, stage: FulfillmentStage, at?: string) => Promise<void>
  /**
   * Flat list of every takeaway/delivery order across all synthetic scopes,
   * newest first. Skips dine-in orders.
   */
  allFulfillmentOrders: () => TableOrder[]
  /** Get tickets for a station across all tables. includeClosed=true keeps finished operations visible. */
  ticketsForStation: (station: StationId, includeClosed?: boolean) => Array<Ticket & { scopeId: string }>
  /** Get all orders for a scope */
  ordersForScope: (scopeId: string) => TableOrder[]
  /** Get orders for a scope filtered by check index */
  ordersForCheck: (scopeId: string, checkIndex: number) => TableOrder[]
  /** How many checks does a scope have (default 1) */
  checksFor: (scopeId: string) => number
  /** Which check tab is active for a scope */
  activeCheckFor: (scopeId: string) => number
  /** Get the active session for a scope (or undefined if not open) */
  sessionFor: (scopeId: string) => TableSession | undefined
  /** Check if a scope has an active session */
  hasSession: (scopeId: string) => boolean
  /** Whether a register is currently open */
  isRegisterOpen: boolean
  /** Get payments recorded for a scope */
  paymentsFor: (scopeId: string) => CheckPayment[]
  /** Check if a specific check has been paid */
  isCheckPaid: (scopeId: string, checkIndex: number) => boolean
  /** Enabled payment methods filtered by active register's linked methods */
  enabledPaymentMethods: PaymentMethod[]
  /** Register scope family configs */
  registerConfigs: RegisterConfig[]
  /** Re-fetch register configs from entity catalog */
  refreshRegisterConfigs: () => void
  /** Currently active register number */
  activeRegisterNum: number
  /** Closed shift history with analytics snapshots */
  closedShifts: RegisterShift[]
  /** Live operational sync status (for data-freshness badges). */
  liveSync: { connected: boolean; loaded: boolean; error: string | null; heartbeatMs: number }
  /** Force an immediate server resync (call after a server-backed mutation). */
  syncNow: () => void
}

const OrderContext = createContext<OrderContextValue | null>(null)

/* ── Provider ───────────────────────────────────────────────────────── */

export function OrderStoreProvider({ children, tenantId }: { children: ReactNode; tenantId?: string | number }) {
  const tenantIdRef = React.useRef<string | number | undefined>(tenantId)
  const [state, dispatch] = useReducer(orderReducer, INITIAL_STATE)

  // Seed default payment methods on first ever load
  useEffect(() => {
    if (state.paymentMethods.length === 0) {
      dispatch({ type: 'SET_PAYMENT_METHODS', payload: DEFAULT_PAYMENT_METHODS })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Hydrate payment methods from entity catalog — merge with defaults.
  // Gated on tenantId (skip while tenant override is mid-switch) and only
  // fires once per resolved tenant. Refire on tenant change handled below.
  const pmHydratedForRef = React.useRef<string | number | null>(null)
  useEffect(() => {
    if (tenantId == null) return
    if (pmHydratedForRef.current === tenantId) return
    pmHydratedForRef.current = tenantId
    fetchPaymentMethodCatalog({ tenant_id: tenantId })
      .then(res => {
        const catalogPms = (res.items ?? []).filter(pm => pm.active)
        if (catalogPms.length === 0) return // no catalog PMs — keep defaults
        const merged: PaymentMethod[] = catalogPms.map(pm => ({
          id:       pm.key,
          label:    pm.label,
          icon:     pm.icon || '💳',
          category: (pm.category || 'cash') as PaymentMethod['category'],
          network:  pm.network || undefined,
          enabled:  true,
        }))
        dispatch({ type: 'SET_PAYMENT_METHODS', payload: merged })
      })
      .catch(err => {
        // 503 / circuit_open: silently keep defaults — upstream is degraded.
        const status = (err as { status?: number } | undefined)?.status
        if (status === 503 || status === 502) return
        console.warn('[useOrderStore] payment method catalog fetch failed:', err)
      })
  }, [tenantId])

  // Hydrate registerConfigs from entity catalog (single source of truth).
  // Tolerates 503 (degraded upstream) silently — defaults remain in state.
  const refreshRegisterConfigs = useCallback(() => {
    fetchRegisterCatalog(tenantId != null ? { tenant_id: tenantId } : undefined)
      .then(res => {
        const configs: RegisterConfig[] = (res.items ?? []).map(r => ({
          number: r.number,
          label: r.label,
          payment_method_ids: r.payment_method_ids,
        }))
        dispatch({ type: 'SET_REGISTER_CONFIGS', payload: configs })
      })
      .catch(err => {
        const status = (err as { status?: number } | undefined)?.status
        if (status === 503 || status === 502) return
        console.warn('[useOrderStore] register catalog fetch failed:', err)
      })
  }, [dispatch, tenantId])

  // Initial hydration on mount — gated on tenantId so the very first render
  // (before tenants resolve) doesn't fire a tenant-less catalog fetch, and
  // re-fires once when tenantId becomes available.
  const regHydratedForRef = React.useRef<string | number | null>(null)
  useEffect(() => {
    if (tenantId == null) return
    if (regHydratedForRef.current === tenantId) return
    regHydratedForRef.current = tenantId
    refreshRegisterConfigs()
  }, [tenantId, refreshRegisterConfigs])

  // ── Live operational sync (server-authoritative, cross-user) ────────
  // The BFF reconstructs the full operational state from the hot_stream and
  // streams it over SSE. Every connected client on the tenant converges on
  // the same state with deterministic ids — no page refresh required.
  const { snapshot: opsSnapshot, loaded: opsLoaded, connected: opsConnected, error: opsError, nudge: opsNudge } =
    useOperationalStream(tenantId)
  const [liveHeartbeatMs, setLiveHeartbeatMs] = React.useState(0)

  // Stable ref so mutation callbacks can request an immediate resync without
  // re-creating on every nudge identity change.
  const opsNudgeRef = React.useRef(opsNudge)
  opsNudgeRef.current = opsNudge

  // Merge each fresh server snapshot over local state.
  useEffect(() => {
    if (!opsSnapshot) return
    setLiveHeartbeatMs(Date.now())
    dispatch({ type: 'APPLY_REMOTE_OPS', payload: opsSnapshot })
  }, [opsSnapshot])

  // Keep the tenantId ref up-to-date and reload state if tenant changes
  useLayoutEffect(() => {
    if (tenantIdRef.current === tenantId) return
    tenantIdRef.current = tenantId
    dispatch({ type: 'REPLACE_STATE', payload: INITIAL_STATE })
  }, [tenantId])

  const placeOrder = useCallback(
    (
      lines: Array<{
        id: number
        scopeKey?: string
        name: string
        description: string
        price: number
        quantity: number
        section: string
        station?: StationId
        note?: string
      }>,
    ) => {
      const checkIndex = state.activeCheck[state.activeScope] ?? 0
      dispatch({ type: 'DISPATCH', payload: { scopeId: state.activeScope, checkIndex, lines } })
    },
    [state.activeScope, state.activeCheck],
  )

  const bumpTicket = useCallback(
    (orderId: string, ticketId: string) => {
      let station: StationId | undefined
      let nextStatus: TicketStatus | null = null
      for (const orders of Object.values(state.tables)) {
        for (const o of orders) {
          if (o.id_order !== orderId) continue
          const t = o.tickets.find(tk => tk.id_ticket === ticketId)
          if (t) {
            station = t.station
            nextStatus = nextTicketStatus(t.status)
            break
          }
        }
        if (station) break
      }

      // 1. Optimistic local bump
      dispatch({ type: 'BUMP_TICKET', payload: { orderId, ticketId } })

      // 2. Fire-and-forget backend bump (non-blocking)
      if (station) {
        const persistedStatus =
          nextStatus === 'in_progress' ||
          nextStatus === 'ready' ||
          nextStatus === 'delivered' ||
          nextStatus === 'closed'
            ? nextStatus
            : undefined

        bumpTicketApi({
          order_id: orderId,
          ticket_id: ticketId,
          station,
          ...(persistedStatus ? { status: persistedStatus } : {}),
        })
          .then(() => { opsNudgeRef.current?.() })
          .catch(err => console.warn('[useOrderStore] backend bump failed (non-fatal):', err))
      }
    },
    [state.tables],
  )

  const markOrderDelivered = useCallback(
    (orderId: string) => {
      // Snapshot ready tickets before optimistic transition so we can persist
      // the same status bump on the backend and avoid refresh regression.
      const readyTickets: Array<{ ticketId: string; station: StationId }> = []
      for (const orders of Object.values(state.tables)) {
        for (const order of orders) {
          if (order.id_order !== orderId) continue
          for (const ticket of order.tickets) {
            if (ticket.status === 'ready') {
              readyTickets.push({ ticketId: ticket.id_ticket, station: ticket.station })
            }
          }
          break
        }
        if (readyTickets.length > 0) break
      }

      dispatch({ type: 'DELIVER_ORDER', payload: { orderId } })

      if (readyTickets.length === 0) return

      Promise.allSettled(
        readyTickets.map(({ ticketId, station }) =>
          bumpTicketApi({ order_id: orderId, ticket_id: ticketId, station, status: 'delivered' }),
        ),
      )
        .then(() => { opsNudgeRef.current?.() })
        .catch((err) => {
          console.warn('[useOrderStore] deliver order backend bump failed (non-fatal):', err)
        })
    },
    [state.tables],
  )

  const discardTicket = useCallback(
    (orderId: string, ticketId: string) => {
      let station: StationId | undefined
      for (const orders of Object.values(state.tables)) {
        for (const o of orders) {
          if (o.id_order !== orderId) continue
          const t = o.tickets.find(tk => tk.id_ticket === ticketId)
          if (t) {
            station = t.station
            break
          }
        }
        if (station) break
      }

      dispatch({ type: 'DISCARD_TICKET', payload: { orderId, ticketId } })

      // Persist as a terminal backend status so KDS/materialized snapshot stays converged.
      if (station) {
        bumpTicketApi({
          order_id: orderId,
          ticket_id: ticketId,
          station,
          status: 'closed',
        })
          .then(() => { opsNudgeRef.current?.() })
          .catch(err => console.warn('[useOrderStore] backend discard failed (non-fatal):', err))
      }
    },
    [state.tables],
  )

  const removeTicketLine = useCallback(
    (orderId: string, ticketId: string, lineIndex: number) => {
      dispatch({ type: 'REMOVE_TICKET_LINE', payload: { orderId, ticketId, lineIndex } })

      let station: StationId | undefined
      let lineSnapshot: Ticket['lines'][number] | undefined
      for (const orders of Object.values(state.tables)) {
        for (const order of orders) {
          if (order.id_order !== orderId) continue
          const ticket = order.tickets.find(tk => tk.id_ticket === ticketId)
          if (!ticket) continue
          station = ticket.station
          lineSnapshot = ticket.lines[lineIndex]
          break
        }
        if (lineSnapshot) break
      }

      voidOrderLineApi({
        order_id: orderId,
        ticket_id: ticketId,
        line_index: lineIndex,
        ...(station ? { station } : {}),
        ...(lineSnapshot?.item_key ? { item_key: lineSnapshot.item_key } : {}),
        ...(lineSnapshot?.name ? { name: lineSnapshot.name } : {}),
        ...(lineSnapshot?.note ? { note: lineSnapshot.note } : {}),
        ...(typeof lineSnapshot?.quantity === 'number' ? { quantity: lineSnapshot.quantity } : {}),
        ...(typeof lineSnapshot?.price === 'number' ? { price: lineSnapshot.price } : {}),
      })
        .then(() => { opsNudgeRef.current?.() })
        .catch(err => {
          console.warn('[useOrderStore] backend line void failed; resyncing optimistic state:', err)
          opsNudgeRef.current?.()
        })
    },
    [state.tables],
  )

  /* ── Fulfillment (takeaway / delivery) helpers ────────────────── */

  /**
   * Generates a short synthetic scope id for fulfillment orders.
   *   takeaway -> TA-0001...   delivery -> DL-0001...
   * Keeps them visually distinct from physical tables (T1, T2, ...) in storage.
   */
  const genFulfillmentScopeId = useCallback((type: 'takeaway' | 'delivery'): string => {
    const prefix = type === 'takeaway' ? 'TA' : 'DL'
    let maxSeq = 0
    const re = new RegExp(`^${prefix}-(\\d{1,})$`, 'i')

    for (const existing of Object.keys(state.tables)) {
      const m = re.exec(existing)
      if (!m) continue
      const n = Number(m[1])
      if (Number.isFinite(n) && n > maxSeq) maxSeq = n
    }

    const next = maxSeq + 1
    return `${prefix}-${String(next).padStart(4, '0')}`
  }, [state.tables])

  const placeFulfillmentOrder = useCallback(
    async (input: {
      fulfillment: Omit<FulfillmentInfo, 'placed_at'> & { placed_at?: string }
      lines: Array<{
        id: number
        scopeKey?: string
        name: string
        description: string
        price: number
        quantity: number
        section: string
        station?: StationId
        note?: string
      }>
    }): Promise<{ scopeId: string }> => {
      if (state.register?.status !== 'open') {
        const err = new Error('register_closed')
        ;(err as Error & { code?: string }).code = 'register_closed'
        throw err
      }

      const scopeId = genFulfillmentScopeId(input.fulfillment.type)
      const fulfillment: FulfillmentInfo = {
        ...input.fulfillment,
        placed_at: input.fulfillment.placed_at ?? new Date().toISOString(),
      }
      dispatch({
        type: 'DISPATCH',
        payload: { scopeId, checkIndex: 0, lines: input.lines, fulfillment },
      })

      try {
        await registerFulfillmentOrder({
          scope_key: scopeId,
          fulfillment_type: input.fulfillment.type,
          shift_id: state.register?.id,
          customer_name: input.fulfillment.customer_name,
          customer_phone: input.fulfillment.customer_phone,
          address: input.fulfillment.address,
          notes: input.fulfillment.notes,
          total: input.lines.reduce((sum, line) => sum + line.price * line.quantity, 0),
          lines: input.lines,
        })
        opsNudgeRef.current?.()
      } catch (err) {
        dispatch({ type: 'ROLLBACK_SCOPE', payload: { scopeId } })
        throw err
      }

      return { scopeId }
    },
    [genFulfillmentScopeId, state.register?.id],
  )

  const payFulfillmentOrder = useCallback(
    async (input: {
      scopeId: string
      amount: number
      tip: number
      paymentMethodId: string
      fulfillmentType?: 'delivery' | 'takeaway'
      paidAt?: string
    }): Promise<void> => {
      const paidAt = input.paidAt ?? new Date().toISOString()
      const syntheticSessionKey = `${input.scopeId}--fulfillment`

      await registerPaymentOutcome({
        session_key: syntheticSessionKey,
        table_key: input.scopeId,
        check_index: 0,
        payment_method: input.paymentMethodId,
        amount: input.amount,
        tip: input.tip,
        paid_at: paidAt,
        shift_id: state.register?.id,
        fulfillment_type: input.fulfillmentType,
      })

      dispatch({
        type: 'PAY_CHECK',
        payload: {
          checkIndex: 0,
          scopeId: input.scopeId,
          paymentMethodId: input.paymentMethodId,
          amount: input.amount,
          tip: input.tip,
          paid_at: paidAt,
        },
      })
    },
    [state.register?.id],
  )

  const markFulfillmentStage = useCallback(
    async (orderId: string, stage: FulfillmentStage, at?: string) => {
      const persistableStageSet = new Set<FulfillmentStage>(['accepted', 'ready', 'handed_off', 'completed'])
      if (!persistableStageSet.has(stage)) {
        // Keep local behavior intact for non-persisted synthetic stages.
        dispatch({ type: 'MARK_FULFILLMENT_STAGE', payload: { orderId, stage, at } })
        return
      }

      const persistableStage = stage as 'accepted' | 'ready' | 'handed_off' | 'completed'

      let scopeKey: string | null = null
      let fulfillmentType: 'delivery' | 'takeaway' | undefined
      let previousStage: FulfillmentStage | null = null

      for (const orders of Object.values(state.tables)) {
        const order = orders.find(o => o.id_order === orderId)
        if (!order) continue
        scopeKey = String(order.id_scope || '').trim() || null
        if (order.fulfillment) {
          previousStage = deriveFulfillmentStage(order)
        }
        const typeRaw = String(order.fulfillment?.type || '').toLowerCase()
        fulfillmentType = (typeRaw === 'delivery' || typeRaw === 'takeaway') ? typeRaw : undefined
        break
      }

      if (!scopeKey) {
        // Fallback for synthetic ids: "<scopeKey>::c<idx>"
        scopeKey = String(orderId).split('::c')[0]?.trim() || null
      }

      if (!scopeKey) {
        throw new Error('missing_scope_key_for_fulfillment_stage')
      }

      const happenedAt = at ?? new Date().toISOString()

      // ── Optimistic update: remove card instantly ───────────────────────
      dispatch({ type: 'MARK_FULFILLMENT_STAGE', payload: { orderId, stage, at: happenedAt } })

      try {
        await registerFulfillmentStage({
          scope_key: scopeKey,
          order_id: orderId,
          stage: persistableStage,
          at: happenedAt,
          fulfillment_type: fulfillmentType,
        })
        opsNudgeRef.current?.()
      } catch (err) {
        console.warn('[markFulfillmentStage] persist failed, rolling back stage:', err)
        if (previousStage && previousStage !== stage) {
          dispatch({ type: 'MARK_FULFILLMENT_STAGE', payload: { orderId, stage: previousStage } })
        }
        throw err
      }
    },
    [state.tables],
  )

  const allFulfillmentOrders = useCallback(
    (): TableOrder[] => {
      const out: TableOrder[] = []
      for (const orders of Object.values(state.tables)) {
        for (const o of orders) if (o.fulfillment) out.push(o)
      }
      // Newest first — placed_at is mandatory on fulfillment.
      out.sort((a, b) =>
        new Date(b.fulfillment!.placed_at).getTime() -
        new Date(a.fulfillment!.placed_at).getTime(),
      )
      return out
    },
    [state.tables],
  )

  /**
   * Auto-stage tracker: whenever ticket statuses change for a fulfillment order,
   * stamp `accepted_at` (any ticket in_progress) and `ready_at` (all ready+).
   * Keeps timestamps in sync with KDS activity — zero manual intervention.
   * `handed_off_at` / `completed_at` remain manual (require operator action).
   */
  useEffect(() => {
    for (const orders of Object.values(state.tables)) {
      for (const o of orders) {
        const f = o.fulfillment
        if (!f || f.completed_at) continue
        if (!f.accepted_at && o.tickets.some(t => t.status === 'in_progress')) {
          dispatch({ type: 'MARK_FULFILLMENT_STAGE', payload: { orderId: o.id_order, stage: 'accepted' } })
        }
        if (!f.ready_at && o.tickets.length > 0 && (o.status === 'fulfilled' || o.status === 'closed')) {
          dispatch({ type: 'MARK_FULFILLMENT_STAGE', payload: { orderId: o.id_order, stage: 'ready' } })
        }
      }
    }
  }, [state.tables])

  const ticketsForStation = useCallback(
    (station: StationId, includeClosed = false): Array<Ticket & { scopeId: string }> => {
      const result: Array<Ticket & { scopeId: string }> = []
      for (const [scopeId, orders] of Object.entries(state.tables)) {
        for (const order of orders) {
          for (const ticket of order.tickets) {
            if (ticket.station === station && (includeClosed || ticket.status !== 'closed')) {
              result.push({ ...ticket, scopeId })
            }
          }
        }
      }
      // Sort: in_progress first, then open, then ready, discarded last
      const priority: Record<TicketStatus, number> = {
        in_progress: 0,
        open: 1,
        ready: 2,
        delivered: 3,
        discarded: 4,
        closed: 5,
      }
      result.sort((a, b) => priority[a.status] - priority[b.status])
      return result
    },
    [state.tables],
  )

  const ordersForScope = useCallback(
    (scopeId: string): TableOrder[] => state.tables[scopeId] ?? [],
    [state.tables],
  )

  const ordersForCheck = useCallback(
    (scopeId: string, checkIndex: number): TableOrder[] =>
      (state.tables[scopeId] ?? []).filter(o => o.checkIndex === checkIndex),
    [state.tables],
  )

  const checksFor = useCallback(
    (scopeId: string): number => state.checksPerScope[scopeId] ?? 1,
    [state.checksPerScope],
  )

  const activeCheckFor = useCallback(
    (scopeId: string): number => state.activeCheck[scopeId] ?? 0,
    [state.activeCheck],
  )

  const sessionFor = useCallback(
    (scopeId: string): TableSession | undefined => state.sessions[scopeId],
    [state.sessions],
  )

  const hasSession = useCallback(
    (scopeId: string): boolean => !!state.sessions[scopeId],
    [state.sessions],
  )

  const isRegisterOpen = state.register?.status === 'open'

  const paymentsFor = useCallback(
    (scopeId: string): CheckPayment[] => state.payments[scopeId] ?? [],
    [state.payments],
  )

  const isCheckPaid = useCallback(
    (scopeId: string, checkIndex: number): boolean =>
      (state.payments[scopeId] ?? []).some(p => p.checkIndex === checkIndex),
    [state.payments],
  )

  const enabledPaymentMethods = React.useMemo(
    () => {
      const config = state.registerConfigs.find(r => r.number === state.activeRegisterNum)
      const linkedIds = config?.payment_method_ids ?? []
      return state.paymentMethods.filter(pm => {
        if (!pm.enabled) return false
        if (linkedIds.length === 0) return true  // empty = all methods
        return linkedIds.includes(pm.id)
      })
    },
    [state.paymentMethods, state.registerConfigs, state.activeRegisterNum],
  )

  const liveSync = React.useMemo(
    () => ({ connected: opsConnected, loaded: opsLoaded, error: opsError, heartbeatMs: liveHeartbeatMs }),
    [opsConnected, opsLoaded, opsError, liveHeartbeatMs],
  )

  const syncNow = useCallback(() => { opsNudgeRef.current?.() }, [])

  return (
    <OrderContext.Provider
      value={{
        state, dispatch, placeOrder, bumpTicket, markOrderDelivered, discardTicket, removeTicketLine,
        placeFulfillmentOrder, markFulfillmentStage, allFulfillmentOrders,
        payFulfillmentOrder,
        ticketsForStation, ordersForScope, ordersForCheck,
        checksFor, activeCheckFor, sessionFor, hasSession,
        isRegisterOpen, paymentsFor, isCheckPaid, enabledPaymentMethods,
        registerConfigs: state.registerConfigs,
        refreshRegisterConfigs,
        activeRegisterNum: state.activeRegisterNum,
        closedShifts: state.closedShifts,
        liveSync,
        syncNow,
      }}
    >
      {children}
    </OrderContext.Provider>
  )
}

/* ── Hook ───────────────────────────────────────────────────────────── */

export function useOrderStore(): OrderContextValue {
  const ctx = useContext(OrderContext)
  if (!ctx) throw new Error('useOrderStore must be used within <OrderStoreProvider>')
  return ctx
}
